// @flow
import Ingest from '@cchome/ingest-react';
import analyticsUtil, {getInstallWorkflowName} from '../utils/AnalyticsUtil';
import ingestUtil from '../utils/IngestUtil';
import appsUtil from '../utils/AppsUtil';
import DeviceDetectionUtil from '../utils/DeviceDetectionUtil';
import AppPickerUtil from '../utils/AppPicker/AppPickerUtil';
import {setExpCampaignAndVariation} from '../utils/TestUtil';
import {APP_PICKER_ANALYTICS, PAGE_TYPE} from '../constants/AppPicker/appPickerConstants';

let logAnalytics = 0;
let logMetricsCollection = 0;
const constants = {
	rsidDev: 'adbem-cchome-dev',
	rsidProd: 'adbem-cchome-prod'
};

export default class AppPickerAnalytics {
	ingest: Object;

	constructor(env: string) {
		const initObj = this.getInitObject(env);

		this.ingest = new Ingest(initObj);

		logMetricsCollection = 0;
		logAnalytics = 0;

		if (window.logAppsMetrics && window.logAppsMetrics > 0) {
			logMetricsCollection = 1;
		}
		if (window.logAnalytics && window.logAnalytics > 0) {
			logAnalytics = 1;
		}
	}

	getInitObject(env: string) {
		const initObj = analyticsUtil.initAnalytics();
		if (env) {
			initObj.environment = env;
		}
		initObj.rsid = constants.rsidDev;
		if (initObj.environment === 'production' || initObj.environment === 'prod') {
			initObj.rsid = constants.rsidProd;
		}
		return initObj;
	}

	/**
	 * [initAnalytics Init Ingest]
	 * @return {[none]}
	 */
	initAnalytics() {
		ingestUtil.initAnalytics(this.ingest);
	}

	/**
	 * [logAnalytics A utility method to set multiple values at once]
	 * @param  {[object of key/value pairs]} dataObject [events]
	 * @return {[none]}
	 */
	logAnalytics(dataObject: Object) {
		ingestUtil.setIngestDataValue(this.ingest, dataObject);
		this.ingest.addToCollection();
	}

	/**
	 * [getCompatibilityString A utility method to get the compatibility string value to be sent in content.type acc. to SDM]
	 * @param  {[bool]} isLatestVersionSupported
	 * @param  {[bool]} isAnyOlderVersionSupported
	 * @return {[string]} compatibility string
	 */
	getCompatibilityString(isLatestVersionSupported: boolean, isAnyOlderVersionSupported: boolean) {
		if (isLatestVersionSupported) {
			return 'Current Version';
		}
		else if (isAnyOlderVersionSupported) {
			return 'Previous Version';
		}
		return 'Incompatible';
	}

	/**
	 * [getPageTypeString A utility method to get the page type string value to be sent in ui.view_type in some places acc. to SDM]
	 * @return {[string]} Welcome or Download Instructions or Incompatible OS View
	 */
	getPageTypeString() {
		if (AppPickerUtil.isNUJAppPickerExperience()) {
			// This is a all-apps or photo plan post-purchase workflow
			if (AppPickerUtil.getPageType() === PAGE_TYPE.INCOMPATIBLE_OS_SCREEN) {
				return 'Incompatible OS View';
			}
			return appsUtil.getPathData().mainAppsTab === 'starter' ? 'Welcome' : 'Download Instructions';
		}
		else if (AppPickerUtil.isSingleAppPlanNUJExp()) {
			// This is a single app plan post-purchase workflow
			return 'Single App Plan';
		}
		return null;
	}

	/**
	 * [getEventSubcategoryString A utility method to get the page type string value to be sent in event.subcategory in some places acc. to SDM]
	 * @return {[string]} Welcome or Download Instructions or Incompatible OS View
	 */
	getEventSubcategoryString() {
		return appsUtil.getPathData().mainAppsTab === 'starter' ? 'Welcome' : 'Download';
	}

	/**
	 * [appending :64 or :32 to os version]
	 * @param  {[text]} osVersion
	 * @return {[text]}  updated osVersion
	 */
	appendBitArchitecture(osVersion: string) {
		let inputOSVersion = osVersion;
		if (appsUtil.isWin()) {
			inputOSVersion += `:${DeviceDetectionUtil.is64bitWindows() ? '64' : '32'}`;
		}
		else if (appsUtil.isMacOS()) {
			inputOSVersion += `:${DeviceDetectionUtil.is64bitMac() ? '64' : '32'}`;
		}
		return inputOSVersion;
	}

	/**
	 * @param {eventObject} genericAnalyticEvent
	 */
	genericAnalyticEvent(eventObject: Object) {
		const dataObject = {};

		// EVENT
		dataObject.setEventCategory = 'WEB';
		if (eventObject.event) {
			dataObject.setEventWorkflow = eventObject.event.workflow || APP_PICKER_ANALYTICS.WORKFLOW;
			dataObject.setEventSubcategory = eventObject.event.subcategory;
			dataObject.setEventType = eventObject.event.type;
			dataObject.setEventSubtype = eventObject.event.subtype;
			dataObject.setEventValue = eventObject.event.value;
			dataObject.setEventGUID = eventObject.event.guid;
			dataObject.setEventOffline = false;
			dataObject.setErrorType = eventObject.event.errortype;
		}

		// Environment
		if (eventObject.env) {
			dataObject.setEnvironmentServiceName = eventObject.env.name;
			dataObject.setEnvironmentServiceVersion = eventObject.env.version;
			dataObject.setEnvComName = eventObject.env.comName;
			dataObject.setComponentVersion = eventObject.env.comVer;
		}

		// SOURCE
		const osVersion = this.appendBitArchitecture(this.ingest.getOSVersion());
		this.ingest.setDataValue('source', 'os_version', osVersion);

		// CONTENT
		if (eventObject.content) {
			dataObject.setContentID = eventObject.content.id;
			dataObject.setContentName = eventObject.content.name;
			dataObject.setContentType = eventObject.content.type;
			dataObject.setContentCategory = eventObject.content.category;
			dataObject.setContentAction = eventObject.content.action;
			dataObject.setContentVersion = eventObject.content.version;
		}

		// UI
		if (eventObject.ui) {
			dataObject.setUIViewType = eventObject.ui.view_type;
			dataObject.setUIFilter = eventObject.ui.filter;
		}
		const sequenceVal = getInstallWorkflowName();
		dataObject.setUISequence = sequenceVal;

		// If not a split event and no explicit override flag for exp data, set campaign and variation based on all running test
		if (!analyticsUtil.isSplitEvent(dataObject) && (!eventObject.exp || !eventObject.exp.override)) {
			setExpCampaignAndVariation(dataObject);
		}
		else if (eventObject.exp) {
			// If split event then set the campaign and variation based on current test
			dataObject.setExpCampaignID = eventObject.exp.campaign_id;
			dataObject.setExpVariationID = eventObject.exp.variation_id;
			dataObject.setExpExperienceID = eventObject.exp.experience_id;
		}

		// CONTEXT
		if (appsUtil.isFFCFallback) {
			// in case of ffc-fallback
			dataObject.setEventContextSequence = 'ffc-fallback';
		}
		dataObject.setIngestContextGuid = analyticsUtil.contextGuid;
		dataObject.setIngestContextParams = analyticsUtil.contextParams;
		dataObject.setIngestContextInit = analyticsUtil.contextInit;

		// User Service Code
		if (AppPickerUtil.isAllAppsPlan()) {
			const gqlProfile = appsUtil.profile && appsUtil.profile.gqlProfile;
			if (gqlProfile) {
				const paidSubscriptions = gqlProfile.account && gqlProfile.account.subscriptions && gqlProfile.account.subscriptions.paid || [];
				const allAppsPlan = paidSubscriptions.find(plan => plan.planCode === 'ccsn_direct_individual');
				if (allAppsPlan) {
					if (allAppsPlan.customerSegment === 'individual' && allAppsPlan.marketSegments && allAppsPlan.marketSegments.find(seg => seg.toLowerCase() === 'edu')) {
						dataObject.setUserServiceCode = 'ccsn_direct_individual_ste';
					}
					else {
						dataObject.setUserServiceCode = 'ccsn_direct_individual';
					}
				}
			}
		}

		this.logAnalytics(dataObject);

		if (logAnalytics) {
			console.log('genericAnalyticEvent: ', dataObject);
		}

		return ingestUtil.sendToIngest(this.ingest, logMetricsCollection);
	}
}
